import { useCallback, useMemo } from 'react'

import {
  type CreatorPermissionView,
  DetailedPermission,
  type TeamMemberDto,
  TeamMemberRole,
} from '@api'
import type { ColumnDef } from '@tanstack/react-table'
import { useUnit } from 'effector-react'
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router'
import * as z from 'zod'

import DeselectIcon from '@/assets/icons/icon-deselect.svg'
import SelectIcon from '@/assets/icons/icon-select.svg'
import { Page } from '@/components/layout/page/page'
import { Button } from '@/components/ui/button.tsx'
import { FormCheckbox } from '@/components/ui/form/form-checkbox'
import { DataTable } from '@/components/ui/table/data-table'
import { Tooltip } from '@/components/ui/tooltip'
import { $creators } from '@/models/creators.ts'
import { OfPermissionsCombobox } from '@/modules/creators/ui/OfPermissionsCombobox.tsx'
import { ONLYFANS_PREFIX } from '@/modules/creators/views/consts.ts'
import { AppRoutes } from '@/utils/constants'
import { classNames } from '@/utils/helpers.ts'

import { useUpdateCreatorAccess } from '../../hooks/use-update-creator-access'
import { CreatorPermissionState } from '../../types'
import { getCreatorAccessEditFormInitialValue } from '../../utils'

export type CreatorAccessEditFormValues = z.infer<
  typeof CreatorsAccessEditFormSchema
>

export const CreatorsAccessEditFormSchema = z.object({
  permissions: z.any(), // Todo
})

export interface Props {
  creatorAccess: CreatorPermissionView
  availablePermissions: DetailedPermission[]
  membersMap: Record<TeamMemberDto['id'], TeamMemberDto>
  creatorId: number
}

export const EditCreatorAccessForm = ({
  creatorAccess,
  membersMap,
  availablePermissions,
  creatorId,
}: Props) => {
  const navigate = useNavigate()
  const { updateCreatorAccessMutation } = useUpdateCreatorAccess(creatorId)
  const creators = useUnit($creators)

  const defaultValues: CreatorAccessEditFormValues = useMemo(
    () => ({
      permissions: getCreatorAccessEditFormInitialValue(
        creatorAccess.members!,
        availablePermissions,
      ),
    }),
    [creatorAccess, availablePermissions],
  )

  const editForm = useForm({
    defaultValues,
  })

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    getValues,
    setValue,
  } = editForm

  const { fields } = useFieldArray({
    name: 'permissions',
    control,
  })

  useWatch({ control: control })

  const handleBackNavigation = () => {
    navigate(AppRoutes.Creators.List.asUrl())
  }

  const isAdminOrOwner = useCallback(
    (memberId: TeamMemberDto['id']) =>
      [TeamMemberRole.ADMIN, TeamMemberRole.LEADER].includes(
        membersMap[memberId].role,
      ),
    [membersMap],
  )

  const ofPermissions = useMemo(
    () =>
      availablePermissions.filter((permission) =>
        permission.value?.startsWith(ONLYFANS_PREFIX),
      ),
    [availablePermissions],
  )

  const nonOfPermissions = useMemo(
    () =>
      availablePermissions.filter(
        (permission) => !permission.value?.startsWith(ONLYFANS_PREFIX),
      ),
    [availablePermissions],
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapPermission: (permission: DetailedPermission) => ColumnDef<any> = ({
    displayName,
    value,
  }: DetailedPermission) => {
    if (!value || !displayName) {
      throw new Error('Permission value and displayName are required')
    }

    return {
      header: displayName,
      accessorKey: value,
      cell: ({ row }) => (
        <Tooltip
          content={
            row.original[value] === CreatorPermissionState.Inherited
              ? 'Inherited from Roles'
              : ''
          }
        >
          <FormCheckbox
            name={`permissions.${row.index}.${value}`}
            // Backend doesn't provide custom admin permissions
            // Permission inherited from Roles are checked and disabled
            disabled={
              isAdminOrOwner(row.original.memberId) ||
              row.original[value] === CreatorPermissionState.Inherited
            }
            checked={
              getValues().permissions[row.index][value] !==
              CreatorPermissionState.Off
            }
            onChange={(state) =>
              setValue(
                `permissions.${row.index}.${value}`,
                state ? CreatorPermissionState.On : CreatorPermissionState.Off,
                { shouldDirty: true },
              )
            }
          />
        </Tooltip>
      ),
    }
  }

  // Array of columns for displaying permissions in the data table
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const accessOnlyfansProfileColumn: ColumnDef<any> = useMemo(
    () => {
      const permission = nonOfPermissions?.find(
        (permission) => permission?.value === 'ACCESS_ONLYFANS_PROFILE',
      )

      if (!permission) {
        throw new Error('ACCESS_ONLYFANS_PROFILE permission not found')
      }

      return mapPermission(permission)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nonOfPermissions],
  )
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const permissionColumns: ColumnDef<any>[] = useMemo(
    () => {
      // Maps availablePermissions to create permission columns with checkbox cell
      return nonOfPermissions
        .filter((permission) => permission.value !== 'ACCESS_ONLYFANS_PROFILE')
        .map(mapPermission)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nonOfPermissions],
  )

  const handleChangeOfPermission = useCallback(
    (index: number, permission: string, value: boolean) => {
      const inheritedPermissions = Object.entries(
        getValues().permissions[index],
      )
        .filter(([, value]) => value === CreatorPermissionState.Inherited)
        .map(([key]) => key)

      setValue(
        `permissions.${index}.${permission}`,
        inheritedPermissions.includes(permission)
          ? CreatorPermissionState.Inherited
          : value
            ? CreatorPermissionState.On
            : CreatorPermissionState.Off,
        {
          shouldDirty: true,
        },
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue],
  )

  // Final columns array for the data table, includes "Member Name" column and permissionColumns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<any>[] = [
    {
      header: 'Member Name',
      accessorKey: 'memberName',
      cell: ({ row }) => {
        const iconClassNames = 'ml-2 w-[0.8rem] h-[0.8rem] cursor-pointer'

        const { memberName, memberId, ...permissions } = getValues(
          `permissions.${row.index}`,
        )
        const hasSelectedFields = Object.values(
          getValues().permissions[row.index],
        ).includes(CreatorPermissionState.On)

        const inheritedPermissions = Object.entries(
          getValues().permissions[row.index],
        )
          .filter(([, value]) => value === CreatorPermissionState.Inherited)
          .map(([key]) => key)

        const selectAllFields = (select: boolean) => {
          if (isAdminOrOwner(row.original.memberId)) {
            return
          }

          for (const permission of Object.keys(permissions)) {
            setValue(
              `permissions.${row.index}.${permission}`,
              inheritedPermissions.includes(permission)
                ? CreatorPermissionState.Inherited
                : select
                  ? CreatorPermissionState.On
                  : CreatorPermissionState.Off,
              {
                shouldDirty: true,
              },
            )
          }
        }

        return (
          <div className="flex items-center justify-between">
            <Tooltip content={memberName}>
              <p className="max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap">
                {row.original.memberName}
              </p>
            </Tooltip>

            {!isAdminOrOwner(row.original.memberId) &&
              (hasSelectedFields ? (
                <DeselectIcon
                  onClick={() => selectAllFields(false)}
                  className={classNames(
                    iconClassNames,
                    'text-danger-default hover:text-danger-dark',
                  )}
                />
              ) : (
                <SelectIcon
                  onClick={() => selectAllFields(true)}
                  className={classNames(
                    iconClassNames,
                    'text-primary-500 hover:text-primary-600',
                  )}
                />
              ))}
          </div>
        )
      },
    },
    accessOnlyfansProfileColumn,
    {
      header: 'OnlyFans Permissions',
      accessorKey: 'onlyfans_permissions',
      cell: ({ row }) => {
        const selectedPermissions = Object.fromEntries(
          Object.entries(getValues().permissions[row.index]).map(
            ([key, value]) => [key, value !== CreatorPermissionState.Off],
          ),
        )
        const disabledPermissions = Object.fromEntries(
          Object.entries(getValues().permissions[row.index])
            .filter(([, value]) => value === CreatorPermissionState.Inherited)
            .map(([key]) => [key, true]),
        )

        return (
          <OfPermissionsCombobox
            index={row.index}
            selectedPermissions={selectedPermissions}
            onChangeOfPermission={handleChangeOfPermission}
            ofPermissions={ofPermissions}
            disabledPermissions={disabledPermissions}
            disabled={
              isAdminOrOwner(row.original.memberId) ||
              getValues().permissions[row.index]['ACCESS_ONLYFANS_PROFILE'] ===
                CreatorPermissionState.Off
            }
          />
        )
      },
    },
    ...permissionColumns,
  ]

  const onSubmit = useCallback(
    // Filter out email cause we cant send it back to the backend
    async ({ permissions }: CreatorAccessEditFormValues) => {
      const data = Object.fromEntries(
        permissions.map(
          ({
            memberId,
            memberName,
            ...permissions
          }: CreatorAccessEditFormValues['permissions']) => [
            memberId,
            Object.entries(permissions)
              .filter(([, value]) => value === CreatorPermissionState.On)
              .map(([key]) => key),
          ],
        ),
      )

      await updateCreatorAccessMutation.mutateAsync({ permissions: data })

      handleBackNavigation()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const { name } = creators.find(({ id }) => id === +creatorId) ?? {}
  const title = 'Access'.concat(name ? ` - ${name}` : '')

  return (
    <FormProvider {...editForm}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Page
          backToPage={{
            name: 'Creators',
            url: AppRoutes.Creators.List.asUrl(),
          }}
          className="mb-2"
          headerOptions={
            <>
              <Button
                onClick={handleBackNavigation}
                type="button"
                variant="outline"
                className="w-32"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!isDirty}
                variant="default"
                className="w-32"
                isLoading={updateCreatorAccessMutation.isLoading}
              >
                Save
              </Button>
            </>
          }
        >
          <DataTable
            title={title}
            data={fields}
            columns={columns}
          />
        </Page>
      </form>
    </FormProvider>
  )
}
